import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SEO from "@/components/SEO";
import { 
  Phone, 
  MessageCircle, 
  Clock, 
  Shield, 
  FileText, 
  Calculator, 
  Car,
  ChevronRight,
  Star,
  CheckCircle,
  Award,
  BadgeCheck,
  FileCheck,
  CircleDollarSign,
  Truck,
  ArrowRight,
  MapPin
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [faqs, setFaqs] = useState([]);
  const [articles, setArticles] = useState([]);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [activeRegion, setActiveRegion] = useState("Hamburg");

  useEffect(() => {
    axios.get(`${API}/faqs`).then(res => setFaqs(res.data.slice(0, 4))).catch(console.error);
    axios.get(`${API}/blog`).then(res => setArticles(res.data)).catch(console.error);
    axios.get(`${API}/service-areas`).then(res => setServiceAreas(res.data)).catch(console.error);
  }, []);

  const services = [
    { id: "unfallgutachten", icon: Car, title: "Unfallgutachten", description: "Präzise Bewertung des Schadensumfangs, der Reparaturkosten und des Wertverlusts nach einem Unfall." },
    { id: "haftpflichtgutachten", icon: Shield, title: "Haftpflichtgutachten", description: "Detaillierte Dokumentation bei unverschuldetem Unfall zur Durchsetzung Ihrer Ansprüche." },
    { id: "kasko-schaden", icon: FileText, title: "Kasko-Schaden", description: "Professionelle Gutachten für Schäden über Ihre Teil- oder Vollkaskoversicherung." },
    { id: "kostenvoranschlag", icon: Calculator, title: "Kostenvoranschlag", description: "Transparente Kostenvoranschläge für Kfz-Reparaturen." },
    { id: "wertgutachten", icon: CircleDollarSign, title: "Wertgutachten", description: "Präzise Bestimmung des aktuellen Marktwerts Ihres Fahrzeugs." },
    { id: "oldtimer-gutachten", icon: Truck, title: "Oldtimer-Gutachten", description: "Detaillierte Bewertung des Wertes Ihres Klassikers." }
  ];

  const steps = [
    { number: "01", title: "Kontakt aufnehmen", description: "Rufen Sie uns an oder schreiben Sie per WhatsApp." },
    { number: "02", title: "Terminvereinbarung", description: "Meist innerhalb von 60 Minuten vor Ort." },
    { number: "03", title: "Fahrzeugbegutachtung", description: "Detaillierte Aufnahme aller Schäden." },
    { number: "04", title: "Gutachtenerstellung", description: "Innerhalb von 24 Stunden fertig." },
    { number: "05", title: "Versicherungsabwicklung", description: "Wir setzen Ihre Ansprüche durch." }
  ];

  const hamburgAreas = serviceAreas.filter(a => a.region === "Hamburg");
  const shAreas = serviceAreas.filter(a => a.region === "Schleswig-Holstein");

  return (
    <>
      <SEO 
        title="Unabhängiger Kfz-Sachverständiger"
        description="Kfz Gutachter Schmeißer - Ihr unabhängiger Kfz-Sachverständiger in Hamburg und Schleswig-Holstein. Unfallgutachten in 24h, innerhalb 60 Min. vor Ort. Kostenlos bei unverschuldetem Unfall. ☎ 0174 893 6514"
        canonical="/"
        keywords="Kfz Gutachter Hamburg, Unfallgutachten, Kfz Sachverständiger, Schleswig-Holstein, Wertgutachten, Schadensgutachten"
      />
      {/* HERO SECTION */}
      <section data-testid="hero-section" className="relative min-h-[90vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <img src="https://static.prod-images.emergentagent.com/jobs/4aee379d-d7a9-43ea-b5e1-4f4900c746b3/images/d7e67a2e521e11a5c3ce3d08a6ad142f684ef2397570e3b16ba9342115f03a44.png" alt="Kfz Gutachter bei der Arbeit" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-gradient"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center space-x-2 bg-[#F7F7F7] px-4 py-2 rounded-sm mb-6 animate-fade-in-up">
              <Clock className="w-4 h-4 text-[#AA6622]" />
              <span className="text-sm font-medium text-[#4A4A4A]">Innerhalb 60 Min. vor Ort</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-black tracking-tight mb-6 animate-fade-in-up animation-delay-100 font-['Outfit']">
              Ihr unabhängiger<br /><span className="text-[#AA6622]">Kfz-Gutachter</span> in<br />Hamburg & Schleswig-Holstein
            </h1>
            <p className="text-lg sm:text-xl text-[#4A4A4A] mb-8 leading-relaxed animate-fade-in-up animation-delay-200">
              Nach einem Unfall benötigen Sie professionelle Unterstützung. Wir erstellen unabhängige Gutachten, damit Sie Ihre Ansprüche fair und schnell durchsetzen können.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-10 animate-fade-in-up animation-delay-300">
              <a href="tel:+491748936514" className="bg-[#AA6622] text-white px-8 py-4 rounded-sm font-bold text-lg flex items-center justify-center space-x-2 hover:bg-[#8e541b] transition-colors" data-testid="hero-cta-phone">
                <Phone className="w-5 h-5" /><span>Schaden melden</span>
              </a>
              <a href="https://wa.me/+491748936514" target="_blank" rel="noopener noreferrer" className="border-2 border-black text-black px-8 py-4 rounded-sm font-bold text-lg flex items-center justify-center space-x-2 hover:bg-black hover:text-white transition-colors" data-testid="hero-cta-whatsapp">
                <MessageCircle className="w-5 h-5" /><span>WhatsApp Chat</span>
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-6 animate-fade-in-up animation-delay-400">
              <div className="flex items-center space-x-2">
                <div className="flex">{[1,2,3,4,5].map(i => <Star key={i} className="w-5 h-5 fill-[#AA6622] text-[#AA6622]" />)}</div>
                <span className="font-bold text-black">5.0</span>
                <span className="text-[#4A4A4A]">bei Google</span>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-1 text-sm text-[#4A4A4A]"><BadgeCheck className="w-4 h-4 text-[#AA6622]" /><span>Zertifiziert</span></div>
                <div className="flex items-center space-x-1 text-sm text-[#4A4A4A]"><Award className="w-4 h-4 text-[#AA6622]" /><span>Meisterbetrieb</span></div>
                <div className="flex items-center space-x-1 text-sm text-[#4A4A4A]"><FileCheck className="w-4 h-4 text-[#AA6622]" /><span>Diplom</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES BAR */}
      <section data-testid="features-bar" className="bg-[#F7F7F7] py-8 border-y border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {[
              { icon: Clock, title: "Schnelle Hilfe", desc: "Innerhalb 60 Min. vor Ort" },
              { icon: Shield, title: "Unabhängig", desc: "Objektive Gutachten" },
              { icon: FileText, title: "Komplette Abwicklung", desc: "Direkt mit der Versicherung" },
              { icon: CheckCircle, title: "Kostenlos", desc: "Bei unverschuldetem Unfall" }
            ].map((f, i) => (
              <div key={i} className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white rounded-sm flex items-center justify-center shadow-sm">
                  <f.icon className="w-6 h-6 text-[#AA6622]" />
                </div>
                <div>
                  <h3 className="font-bold text-black text-sm sm:text-base">{f.title}</h3>
                  <p className="text-[#4A4A4A] text-xs sm:text-sm">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section data-testid="services-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#AA6622] font-bold text-sm uppercase tracking-wider mb-4">Unsere Leistungen</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-6 font-['Outfit']">Umfassende Kfz-Gutachten für jeden Bedarf</h2>
            <p className="text-lg text-[#4A4A4A] max-w-2xl mx-auto">Von Unfallschäden bis zur Oldtimer-Bewertung – professionelle Gutachten für alle Fahrzeugsituationen.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <Link key={service.id} to={`/leistungen/${service.id}`} data-testid={`service-card-${service.id}`} className="bg-[#F7F7F7] p-8 rounded-sm card-hover group">
                <div className="w-14 h-14 bg-white rounded-sm flex items-center justify-center mb-6 shadow-sm group-hover:bg-[#AA6622] transition-colors">
                  <service.icon className="w-7 h-7 text-[#AA6622] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-black mb-3 font-['Outfit']">{service.title}</h3>
                <p className="text-[#4A4A4A] mb-6 leading-relaxed">{service.description}</p>
                <span className="inline-flex items-center text-[#AA6622] font-bold group-hover:text-[#8e541b] transition-colors">
                  <span>Mehr erfahren</span><ChevronRight className="w-4 h-4 ml-1" />
                </span>
              </Link>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/leistungen" className="inline-flex items-center space-x-2 border-2 border-black text-black px-8 py-4 rounded-sm font-bold hover:bg-black hover:text-white transition-colors">
              <span>Alle Leistungen anzeigen</span><ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* PROCESS SECTION */}
      <section data-testid="process-section" className="py-20 md:py-32 bg-[#F7F7F7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#AA6622] font-bold text-sm uppercase tracking-wider mb-4">So funktioniert's</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-6 font-['Outfit']">In 5 Schritten zu Ihrem Gutachten</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {steps.map((step, index) => (
              <div key={index} data-testid={`process-step-${index + 1}`} className={`bg-white p-6 rounded-sm relative ${index < 4 ? 'timeline-connector' : ''}`}>
                <div className="text-5xl font-black text-[#AA6622]/20 mb-4 font-['Outfit']">{step.number}</div>
                <h3 className="text-lg font-bold text-black mb-2 font-['Outfit']">{step.title}</h3>
                <p className="text-sm text-[#4A4A4A] leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <a href="tel:+491748936514" className="inline-flex items-center space-x-2 bg-[#AA6622] text-white px-8 py-4 rounded-sm font-bold text-lg hover:bg-[#8e541b] transition-colors" data-testid="process-cta">
              <Phone className="w-5 h-5" /><span>Jetzt Schaden melden</span>
            </a>
          </div>
        </div>
      </section>

      {/* SERVICE AREAS SECTION */}
      <section data-testid="service-areas-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#AA6622] font-bold text-sm uppercase tracking-wider mb-4">Einsatzgebiete</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-6 font-['Outfit']">Wir sind in Ihrer Nähe</h2>
            <p className="text-lg text-[#4A4A4A]">Hamburg und Schleswig-Holstein – innerhalb kürzester Zeit bei Ihnen.</p>
          </div>
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-[#F7F7F7] p-1 rounded-sm">
              <button onClick={() => setActiveRegion("Hamburg")} className={`px-6 py-3 rounded-sm font-bold transition-colors ${activeRegion === "Hamburg" ? "bg-[#AA6622] text-white" : "text-[#4A4A4A] hover:text-black"}`} data-testid="tab-hamburg">Hamburg</button>
              <button onClick={() => setActiveRegion("Schleswig-Holstein")} className={`px-6 py-3 rounded-sm font-bold transition-colors ${activeRegion === "Schleswig-Holstein" ? "bg-[#AA6622] text-white" : "text-[#4A4A4A] hover:text-black"}`} data-testid="tab-sh">Schleswig-Holstein</button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {(activeRegion === "Hamburg" ? hamburgAreas : shAreas).map((area) => (
              <Link key={area.id} to={`/einsatzgebiete/${area.slug}`} data-testid={`area-${area.slug}`} className="bg-[#F7F7F7] p-4 rounded-sm hover:bg-[#AA6622] hover:text-white transition-colors cursor-pointer group">
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-[#AA6622] group-hover:text-white" />
                  <span className="font-medium text-sm">{area.name}</span>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/einsatzgebiete" className="inline-flex items-center space-x-2 border-2 border-black text-black px-8 py-4 rounded-sm font-bold hover:bg-black hover:text-white transition-colors">
              <span>Alle Einsatzgebiete anzeigen</span><ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* GOOGLE REVIEWS SECTION */}
      <section data-testid="testimonials-section" className="py-20 md:py-32 bg-[#F7F7F7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block text-[#AA6622] font-bold text-sm uppercase tracking-wider mb-4">Kundenstimmen</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-6 font-['Outfit']">Was unsere Kunden sagen</h2>
          </div>
          {/* Elfsight Google Reviews Widget */}
          <div className="elfsight-app-299e5b52-0d44-40ba-9281-ccdbc9d5cf09" data-elfsight-app-lazy></div>
        </div>
      </section>

      {/* FAQ PREVIEW */}
      <section data-testid="faq-preview-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#AA6622] font-bold text-sm uppercase tracking-wider mb-4">FAQ</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-6 font-['Outfit']">Häufig gestellte Fragen</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) => (
              <AccordionItem key={faq.id} value={faq.id} className="bg-[#F7F7F7] rounded-sm border-none">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-100 font-bold text-left text-black">{faq.question}</AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-[#4A4A4A] leading-relaxed">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-12">
            <Link to="/faq" className="inline-flex items-center space-x-2 text-[#AA6622] font-bold hover:text-[#8e541b] transition-colors">
              <span>Alle FAQ anzeigen</span><ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section data-testid="blog-section" className="py-20 md:py-32 bg-[#F7F7F7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#AA6622] font-bold text-sm uppercase tracking-wider mb-4">Ratgeber</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-6 font-['Outfit']">Wissen rund ums Kfz-Gutachten</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {articles.map((article) => (
              <Link key={article.id} to={`/ratgeber/${article.slug}`} data-testid={`blog-article-${article.id}`} className="bg-white rounded-sm overflow-hidden card-hover group">
                <div className="aspect-video overflow-hidden">
                  <img src={article.image} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-4 text-sm text-[#4A4A4A] mb-3">
                    <span className="bg-[#AA6622]/10 text-[#AA6622] px-2 py-1 rounded-sm font-medium">{article.category}</span>
                    <span>{article.read_time}</span>
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3 font-['Outfit']">{article.title}</h3>
                  <p className="text-[#4A4A4A] leading-relaxed">{article.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/ratgeber" className="inline-flex items-center space-x-2 border-2 border-black text-black px-8 py-4 rounded-sm font-bold hover:bg-black hover:text-white transition-colors">
              <span>Alle Ratgeber-Artikel</span><ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
